import { useEffect, useCallback } from 'react';

/**
 * Hook for managing communication between editor and iframe preview
 *
 * @param {React.RefObject} iframeRef - Reference to the iframe element
 * @param {Object} callbacks - Callback functions for various events
 * @param {Function} callbacks.onSectionSelect - Called when a section is clicked in preview
 * @param {Function} callbacks.onElementSelect - Called when an element is clicked in preview
 * @param {Function} callbacks.onHover - Called when an element is hovered in preview
 * @param {Function} callbacks.onZoom - Called when pinch-to-zoom is detected in preview
 * @param {Function} callbacks.onInlineEditStart - Called when inline text editing starts
 * @param {Function} callbacks.onInlineEditEnd - Called when inline text editing ends with new content
 * @param {Function} callbacks.onDuplicate - Called when Ctrl/Cmd+D is pressed to duplicate selected element
 * @param {Function} callbacks.onDelete - Called when Ctrl/Cmd+Backspace is pressed to delete selected element (with confirmation)
 * @param {Function} callbacks.onForceDelete - Called when Ctrl/Cmd+Shift+D is pressed to force delete selected element
 */
export function usePreviewBridge(iframeRef, callbacks = {}) {
  const { onSectionSelect, onElementSelect, onHover, onZoom, onInlineEditStart, onInlineEditEnd, onInlineEditCancel, onDuplicate, onDelete, onForceDelete, onPlacementClick, onPlacementCancel, onSectionPlacementClick } = callbacks;

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Only handle messages from our iframe
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) {
        return;
      }

      const { type, sectionId, elementPath } = event.data || {};

      switch (type) {
        case 'PREVIEW_SELECT':
          if (elementPath) {
            onElementSelect?.(sectionId, elementPath);
          } else if (sectionId) {
            onSectionSelect?.(sectionId);
          }
          break;
        case 'PREVIEW_HOVER':
          onHover?.(sectionId, elementPath);
          break;
        case 'PREVIEW_ZOOM':
          onZoom?.(event.data.deltaY);
          break;
        case 'INLINE_EDIT_START':
          onInlineEditStart?.(sectionId, elementPath);
          break;
        case 'INLINE_EDIT_END':
          onInlineEditEnd?.(sectionId, elementPath, event.data.newContent);
          break;
        case 'INLINE_EDIT_CANCEL':
          onInlineEditCancel?.();
          break;
        case 'DUPLICATE_SELECTED':
          onDuplicate?.();
          break;
        case 'DELETE_SELECTED':
          onDelete?.();
          break;
        case 'FORCE_DELETE_SELECTED':
          onForceDelete?.();
          break;
        case 'PLACEMENT_CLICK':
          onPlacementClick?.(event.data.sectionId, event.data.parentPath, event.data.index);
          break;
        case 'SECTION_PLACEMENT_CLICK':
          onSectionPlacementClick?.(event.data.index);
          break;
        case 'PLACEMENT_CANCEL':
          onPlacementCancel?.();
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeRef, onSectionSelect, onElementSelect, onHover, onZoom, onInlineEditStart, onInlineEditEnd, onInlineEditCancel, onDuplicate, onDelete, onForceDelete, onPlacementClick, onPlacementCancel, onSectionPlacementClick]);

  // Method to update selection highlight in iframe
  const updateSelection = useCallback((sectionId, elementPath = null) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    // Remove previous selection
    doc.querySelectorAll('.selected').forEach(el => {
      el.classList.remove('selected');
    });

    // Add selection to new element
    if (elementPath && sectionId) {
      // Find the element within the correct section to avoid path collisions across sections
      const section = doc.querySelector(`[data-section-id="${sectionId}"]`);
      if (section) {
        const el = section.querySelector(`[data-element-id="${elementPath}"]`);
        el?.classList.add('selected');
      } else {
        console.warn('[usePreviewBridge] Section not found:', sectionId);
      }
    } else if (sectionId) {
      const el = doc.querySelector(`[data-section-id="${sectionId}"]`);
      el?.classList.add('selected');
    }
  }, [iframeRef]);

  // Method to scroll element into view in iframe
  const scrollToElement = useCallback((sectionId, elementPath = null) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    let el = null;
    if (elementPath && sectionId) {
      // Find element within the section to avoid path collisions
      const section = doc.querySelector(`[data-section-id="${sectionId}"]`);
      if (section) {
        el = section.querySelector(`[data-element-id="${elementPath}"]`);
      }
    } else if (sectionId) {
      el = doc.querySelector(`[data-section-id="${sectionId}"]`);
    }

    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [iframeRef]);

  // Method to enter placement mode in iframe
  const enterPlacementMode = useCallback((componentType, placementLevel) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage({ type: 'ENTER_PLACEMENT_MODE', componentType, placementLevel: placementLevel || 'component' }, '*');
  }, [iframeRef]);

  // Method to exit placement mode in iframe
  const exitPlacementMode = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage({ type: 'EXIT_PLACEMENT_MODE' }, '*');
  }, [iframeRef]);

  return {
    updateSelection,
    scrollToElement,
    enterPlacementMode,
    exitPlacementMode,
  };
}

export default usePreviewBridge;
